import classNames from 'classnames';
import { type ComponentProps, memo, useMemo } from 'react';

import { type Clip, ClipStatus } from '@air/api/types';
import { ImgixImage } from '@air/component-imgix-image';
import { formatDateVerbose } from '@air/utilities';

import { AssetMobileVersionsListItemThumbnailPreview } from '~/_components/AssetMobileVersionsListItemThumbnailPreview';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';

export type AssetMobileVersionsItemProps = Pick<ComponentProps<'button'>, 'onClick'> & {
  isActive?: boolean;
  version: Clip;
};

export const AssetMobileVersionsItem = memo(({ isActive, onClick, version }: AssetMobileVersionsItemProps) => {
  const showPreview = useMemo(() => {
    return version.status === ClipStatus.nonTranscodable || version.status === ClipStatus.failed;
  }, [version.status]);

  const fallbackUrl = useMemo(() => {
    if (!version.assets.image) {
      return '';
    }

    return getCloudFrontUrlFromImgixUrl(version.assets.image);
  }, [version.assets.image]);

  const thumbnail = useMemo(() => {
    if (showPreview) {
      return <AssetMobileVersionsListItemThumbnailPreview ext={version.ext} />;
    }

    if (!version.assets.image) {
      return null;
    }

    return (
      <ImgixImage
        alt={version.title || version.importedName || ''}
        src={version.assets.image}
        fallbackUrl={fallbackUrl}
        imgixParams={{ fit: 'crop', w: 96, h: 64, cs: 'origin' }}
        height={64}
        width={96}
      />
    );
  }, [fallbackUrl, showPreview, version.assets.image, version.ext, version.importedName, version.title]);

  return (
    <button className="flex w-full items-center gap-4 text-left" onClick={onClick} type="button">
      <div
        className={classNames(
          'pointer-events-none relative h-16 w-24 shrink-0 overflow-hidden rounded-2',
          isActive && 'ads:ring-primary ring-2 ring-blue-9 ring-offset-1 ring-offset-white dark:ring-offset-black',
        )}
      >
        <div className="absolute right-1 bottom-1 rounded-2 bg-black/80 px-1 py-0.5 font-bold ads:text-primary text-10 text-gray-12 uppercase tracking-wider">
          V{version.version}
        </div>
        {thumbnail}
      </div>
      <div className="grow">
        <div className="truncate font-semibold ads:text-primary text-14 text-gray-12">
          {version.title || version.importedName}
        </div>
        <div className="font-medium ads:text-secondary text-12 text-gray-11">
          {formatDateVerbose(version.createdAt)}
        </div>
      </div>
    </button>
  );
});

AssetMobileVersionsItem.displayName = 'AssetMobileVersionsItem';
